import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PropTypes from "prop-types";
import "./styles.css";

const ORDER_NUMBER_REGEX = /^[0-9]*$/;
const SERIAL_NUMBER_REGEX = /^[a-zA-Z0-9]*$/;

const TermsAndConditions = ({ onAccept, serialError, setSerialError }) => {
  const [orderNumber, setOrderNumber] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [isNextPressed, setIsNextPressed] = useState(false);
  const [isValidOInput, setIsValidOInput] = useState(true);
  const [isValidSInput, setIsValidSInput] = useState(true);
  const [serialNumber, setSerialNumber] = useState("");
  const [error, setError] = useState("");
  const [jobType, setJobType] = useState("");

  const handleCheckboxChange = (event) => {
    setIsChecked(event.target.checked);
    setError("");
  };
  const handleOInputChange = (inputValue) => {
    if (ORDER_NUMBER_REGEX.test(inputValue)) {
      setOrderNumber(inputValue.slice(0, 20));
      setIsValidOInput(true);
    } else {
      setIsValidOInput(false);
    }
  };
  const handleSInputChange = (inputValue) => {
    if (SERIAL_NUMBER_REGEX.test(inputValue)) {
      setSerialNumber(inputValue.toUpperCase().slice(0, 30));
      setIsValidSInput(true);
    } else {
      setIsValidSInput(false);
    }
    setIsNextPressed(false);
    setSerialError(false);
  };

  const handleAccept = () => {
    setIsNextPressed(true);
    setIsValidSInput(true);
    if (!isChecked) {
      setError("Please accept the terms and conditions.");
      setSerialError(false);
    } else if (!serialNumber) {
      setError("Please enter a serial number.");
      setSerialError(false);
    } else {
      setError("");
      onAccept(orderNumber, jobType, serialNumber);
    }
  };

  const handleJobTypeChange = (event) => {
    setJobType(event.target.value);
  };

  return (
    <Box className="terms-box">
      <div className="terms-headers">
        <h1 className="title">INSTALLER REGISTRATION</h1>
      </div>
      <div className="containertnc">
        <p className="terms-description" id="popup-description">
          Installation Terms and Conditions
        </p>
        <div className="terms-message">
          By checking this box, I attest that I am qualified to perform this
          installation pursuant to the requirements of the National Electric
          Code, local building codes, and local ordinances. This may include
          being a licensed electrician that is active and in good standing with
          the state in which I am performing these installation services, or
          working under the direction of such licensed electrician. I have the
          appropriate training, knowledge, and experience to perform
          installation or maintenance for this system and will follow all
          applicable laws and code requirements. I will configure the system in
          line with the permitted design and all current utility requirements.
        </div>
        <div className="input-button">
          <div className="optional-message">
            Please enter the GM Energy PowerShift Charger (EVSE) or Home Hub
            (MID) serial number for the site you are planning to commission. To
            locate the serial number please check the Quick Installation Guide
            (QIG) or the bottom of the hardware product.
          </div>
          <div className="optional-input">
            <TextField
              variant="outlined"
              size="small"
              value={orderNumber}
              onChange={(e) => handleOInputChange(e.target.value || '')}
              className="order-input"
              placeholder="Order Number"
              InputProps={{
                style: {
                  color: "#000",
                },
              }}
              error={!isValidOInput}
              helperText={
                isValidOInput ? "" : "Only digits 0 to 9 are allowed."
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#000",
                    borderWidth: "1px",
                  },
                  "&.Mui-focused": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#000",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputLabel-outlined": {
                    fontWeight: "bold",
                  },
                },
              }}
            />
            <TextField
              variant="outlined"
              size="small"
              value={serialNumber}
              onChange={(e) => handleSInputChange(e.target.value || '')}
              className="order-input"
              placeholder="Serial Number*"
              InputProps={{
                style: {
                  color: "#000",
                },
              }}
              error={!isValidSInput || (isNextPressed && !serialNumber)}
              helperText={
                <>
                  {isNextPressed && !serialNumber
                    ? "Please enter a serial number."
                    : ""}
                  {isValidSInput ? "" : "Only letters and numbers are allowed."}
                </>
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#000",
                    borderWidth: "1px",
                  },
                  "&.Mui-focused": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#000",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputLabel-outlined": {
                    fontWeight: "bold",
                  },
                },
              }}
            />
            <Select
              value={jobType}
              onChange={handleJobTypeChange}
              className="job-type"
              displayEmpty
              size="small"
              sx={{
                fontSize: "12px",
                textAlign: "left",
                color: jobType === "" ? "gray" : "black",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#000",
                  borderWidth: "1px",
                },
                "&.Mui-focused": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#000",
                    borderWidth: "2px",
                  },
                },
              }}
            >
              <MenuItem value="">What type of job are you performing?</MenuItem>
              <MenuItem value="newInstallation">New Installation</MenuItem>
              <MenuItem value="maintenanceRepairs">
                Maintenance/Repairs
              </MenuItem>
            </Select>
          </div>
          <div className="accept-checkbox">
            <FormControlLabel
              control={
                <Checkbox
                  checked={isChecked}
                  onChange={handleCheckboxChange}
                  color="primary"
                  style={{ color: error && !isChecked ? "red" : "inherit" }}
                />
              }
              label={
                <span className="checkbox-label">
                  I accept the Installation Terms and Conditions.
                </span>
              }
            />
            {error && !isChecked && (
              <p className="error-text">
                {error.includes("terms") &&
                  "Please accept the terms and conditions."}
              </p>
            )}
          </div>
          <div className="next-button">
            <Button variant="contained" color="primary" onClick={handleAccept}  sx={{backgroundColor: '#000', '&:hover': { backgroundColor: '#0009FF' } }}>
              Accept
            </Button>
          </div>
          {serialError && (
            <div className="serial-error">
              <ErrorOutlineIcon className="error-icon" />
              Unable to complete request, please retry later.
            </div>
          )}
        </div>
      </div>
    </Box>
  );
};

TermsAndConditions.propTypes = {
  onAccept: PropTypes.func.isRequired,
};

export default TermsAndConditions;
